<script setup lang="ts">
import {
  STEP_ANSWER_CONTENT_TYPE,
} from '@models/quiz';
import { computed } from 'vue';

type Props = {
  modelValue: any
  value?: any
  type?: 'checkbox' | 'radio'
  size?: number | string
  name?: string
  styled?: boolean
  variant?: 'default' | 'auth'
}

const props = withDefaults(defineProps<Props>(), {
  value: false,
  type: STEP_ANSWER_CONTENT_TYPE.CHECKBOX,
  size: 20,
  name: '',
  styled: false,
  variant: 'default',
});

const emit = defineEmits<{ 'update:modelValue': [value: any] }>();

const model = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const isAuthVariant = computed(() => props.variant === 'auth');

const fieldSizeStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));
</script>

<template>
  <label
    v-if="isAuthVariant"
    class="flex cursor-pointer items-start gap-12"
    data-testpl="checkbox-wrapper"
  >
    <span
      class="relative mt-1 flex size-24 shrink-0 items-center justify-center rounded-lg border-2 transition-colors"
      :class="model
        ? 'border-off-black-500 bg-off-black-500'
        : 'border-off-black-500 bg-transparent'"
      data-testpl="checkbox-input_fake"
    >
      <input
        v-model="model"
        :value="value"
        class="absolute inset-0 cursor-pointer opacity-0"
        :type="type"
        :name="name"
        data-testpl="checkbox-input"
      >
      <svg
        v-show="model"
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="10"
        viewBox="0 0 14 10"
        fill="none"
        class="pointer-events-none text-white"
        aria-hidden="true"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 5 4 4 8-8"
        />
      </svg>
    </span>
    <span
      class="text-12 text-dark-grey-500"
      data-testpl="checkbox-description_text"
    >
      <slot />
    </span>
  </label>

  <label
    v-else
    class="base-check"
    :class="{ 'base-check--styled': styled }"
    data-testpl="checkbox-wrapper"
  >
    <input
      v-model="model"
      :value="value"
      class="base-check__input"
      :type="type"
      :name="name"
      data-testpl="checkbox-input"
    >
    <span
      class="base-check__field"
      :style="fieldSizeStyle"
      data-testpl="checkbox-input_fake"
    />
    <span
      class="text flex grow items-center justify-between gap-16 self-center"
      data-testpl="checkbox-description_text"
    >
      <slot />
    </span>
  </label>
</template>

<style lang="scss" scoped>
.base-check {
  position: relative;
  display: flex;
  cursor: pointer;

  &--styled {
    gap: 20px;
    padding: 11px;
    border-radius: 16px;
    background-color: var(--color-white);
    box-shadow: $soft-shadow;
  }
}

.base-check__input {
  position: absolute;
  pointer-events: none;
  opacity: 0;
}

.base-check__field {
  display: flex;
  flex-shrink: 0;
  border-radius: 30%;
  border: 2px solid var(--color-warm-grey);
  background-color: var(--color-white);
  overflow: hidden;
  transition: border-color 0.3s;

  &::before {
    content: '';
    width: 100%;
    border-radius: 30%;
    background-color: theme('colors.turquoise');
    transition-property: transform, opacity;
    transition-duration: 0.3s;
    transform: scale(0);
    opacity: 0;
  }

  .base-check__input:checked + &::before {
    transform: scale(0.7);
    opacity: 1;
  }

  .base-check:hover &,
  .base-check__input:focus + & {
    border-color: theme('colors.turquoise');
  }
}
</style>
